<template>
  <div class="jps-mysearch jps-layout-sm">
    <UiSideMenu class="mysearch-menu" :is-clicked="isClicked">
      <div class="side-menu-item">
        <BaseLink to="/mynote">{{
          ls({ ja: 'マイノート', en: 'My Notes' })
        }}</BaseLink>
      </div>
      <div class="side-menu-item">
        <BaseLink to="/mygallery">{{
          ls({ ja: 'マイギャラリー', en: 'My Gallery' })
        }}</BaseLink>
      </div>
      <UiSideMenuList class="side-menu-item">
        <template #head>
          <h3>
            {{ ls({ ja: 'マイサーチ', en: 'My Search' }) }}
          </h3>
          <button @click="sortCs()">
            {{ ls({ ja: '並べ替え', en: 'Sort' }) }}
          </button>
        </template>
        <template #list>
          <li v-for="cs in css" :key="cs.id">
            <button
              class="cs-list-item"
              :class="{
                'jps-active':
                  localSearchStore.activeMySearch === cs.id,
              }"
              @click.stop="() => {
                activate(cs.id)
                isClicked = !isClicked
              }"
            >
              <BaseIcon name="mdi/magnify" />
              {{ ls(cs.cs.name) }}
            </button>
          </li>
        </template>
      </UiSideMenuList>
    </UiSideMenu>
    <MysearchEditor
      class="mysearch-main"
      v-if="mode === 'edit'"
      @finish="mode = 'view'"
    />
    <MysearchViewer
      class="mysearch-main"
      v-else
      @add="addCs"
      @delete="deleteCs"
      @copy="copyCs"
      @import="importCs"
      @export="exportCs"
      @edit="mode = 'edit'"
    />
    <UiInputModal ref="inputModal" />
    <UiConfirmModal ref="confirmModal" />
    <CsImportModal ref="csImportModal" />
    <UiSortModal ref="sortModal" :renderer="(ws) => ls(ws.cs.name)" />
  </div>
</template>

<script lang="ts" setup>
definePageMeta({
  disableFooter: true,
  disableDefaultMargin: true,
})
import { dapLangGlobal } from '#imports'
import type {
  UiConfirmModal,
  UiInputModal,
  UiSortModal,
  CsImportModal,
} from '#components'

const { getPageAndCache } = usePage()
const { meta } = await getPageAndCache()
useHead(meta)

const isClicked = ref(false)

const { ls, lst } = useI18n()
const { snackbarSuccess } = useSnackbar()
const { loadingExecuteAndNotify } = useLoader()
const localSearchStore = useLocalSearchStore()
const csStore = useCsStore()
const { initCsForEdit } = useCsEditor()

const css: Ref<CsEntry[]> = ref([])
const mode: Ref<'view' | 'edit'> = ref('view')

const sync = async () => {
  css.value = await csStore.getAll()
}

onBeforeMount(async () => {
  await sync()
  csStore.addListener(sync)
})

onBeforeUnmount(() => {
  csStore.removeListener(sync)
})

const activate = (id: string) => {
  mode.value = 'view'
  localSearchStore.activeMySearch = id
}

const inputModal = ref<InstanceType<typeof UiInputModal>>()
const addCs = async () => {
  try {
    const newCs = await inputModal.value.openAwait({
      title: { ja: '新しいカスタム検索の名前', en: 'Title for new custom search' },
    })
    if (!newCs) return
    localSearchStore.activeMySearch = await csStore.addCs(
      {
        name: { ja: newCs },
        keywordFields: ['keywords'],
      },
      css.value.length,
    )
  } catch {}
}

const sortModal = ref<InstanceType<typeof UiSortModal>>()
const sortCs = async () => {
  try {
    const sorted = await sortModal.value.openAwait(clone(css.value))
    if (!sorted) return
    await csStore.sort(sorted.map((we: CsEntry) => we.id))
  } catch {}
}

const copyCs = async () => {
  if (!localSearchStore.activeMySearch) return
  const ws = await csStore.getCs(localSearchStore.activeMySearch)
  const text = await inputModal.value.openAwait({
    title: { ja: 'コピー先の名前',  en: 'Name for copied custom search' },
  })
  if (!text) return
  const cs = clone(ws.cs)
  cs.name = { ja: text }
  await csStore.addCs(cs, css.value.length)
  snackbarSuccess({
    ja: text + 'をコピーしました',
    en: text + ' is copied',
  })
}

const confirmModal = ref<InstanceType<typeof UiConfirmModal>>()
const deleteCs = async () => {
  try {
    if (!localSearchStore.activeMySearch) return
    const cs = await csStore.getCs(localSearchStore.activeMySearch)
    const confirm = await confirmModal.value.openAwait({
      message: dapLangGlobal.deleteConfirm,
      messageVars: [ls(cs.cs.name)]
    })
    if (!confirm) return
    await loadingExecuteAndNotify(
      lst(dapLangGlobal.deleteSuccess, ls(cs.cs.name)),
      csStore.deleteCs(localSearchStore.activeMySearch)
    )
    nextTick(async () => {
      await sync()
      localSearchStore.activeMySearch =
        css.value.length > 0 ? css.value[0].id : null
    })
  } catch (cancel) {
    console.info(cancel)
  }
}

const csImportModal = ref<InstanceType<typeof CsImportModal>>()
const importCs = async () => {
  try {
    const cs = await csImportModal.value.openAwait()
    if (!cs) return
    initCsForEdit(cs)
    const csimport = clone(cs)
    await csStore.addCs(csimport, css.value.length)
    snackbarSuccess({
      ja: ls(csimport.name) + 'をインポートしました',
      en: ls(csimport.name) + ' is imported',
    })
  } catch {}
}

const exportCs = async () => {
  try {
    const csEntry = await csStore.getCs(
      localSearchStore.activeMySearch,
    )
    const cs: CustomSearchDefinition = clone(csEntry.cs)
    delete cs.edit
    delete cs.permission
    exportJSON(cs, ls(cs.name) + '.json')
  } catch {}
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-mysearch {
  height: $partial-scroll-height;
  display: flex;
  background-color: $jps-gray-50;
  .mysearch-main {
    flex-grow: 1;
  }
}
</style>
